import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Archive, BarChart3, Calendar, Clock, Code2, Copy, Eye, Globe, HelpCircle, Layers, ListChecks,
  Pencil, RefreshCcw, Send, Settings, Shield, Shuffle, Tag, Trash2, Users, X,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useHasPlanFeature } from "../../contexts/PlanContext";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  AppDataTable, Callout, ConfirmModal, DetailRow, DetailSection, EmptyState, IconButton, LinkedText,
  PageHeader, StatsCard, StatsGrid, StatusBadge,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import SectionMark from "./Builder/SectionMark";
import { DIFFICULTY_LABELS, QUESTION_TYPE_LABELS, letterOf } from "./Builder/builderRules";
import { assessmentTrail } from "../QuizBank/quizCenterTrail";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned exam" };
const STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  published: { label: "Published", tone: "success" },
  archived: { label: "Archived", tone: "warning" },
};
const AUDIENCE_LABELS = {
  batch: "Batches",
  course: "Courses",
  direct: "Specific students",
  open: "Every student",
  rules: "Different rules per group",
  public: "Public exams only",
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const yesNo = (v) => (v ? "Yes" : "No");
const day = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const idOf = (v) => String(v?._id || v || "");
const questionMarks = (qi) => Number(qi.overrideMarks ?? qi.question?.marks ?? 1) || 0;
const ruleTotal = (rules) => (rules || []).reduce((sum, r) => sum + (Number(r.count) || 0), 0);

/**
 * The paper's marks, when they can be known before anyone sits it — not for a
 * random draw, nor for a random subset of a pool, whose marks follow the draw.
 */
function paperMarks(a) {
  if (a.isSectioned && a.sections?.length) {
    let total = 0;
    for (const s of a.sections) {
      if (s.dynamicConfig?.rules?.length) return null;
      total += (s.questions || []).reduce((sum, qi) => sum + questionMarks(qi), 0);
      total += (s.codingQuestions || []).reduce((sum, c) => sum + (Number(c.marks ?? 10) || 0), 0);
    }
    return total;
  }
  if (a.selectionMode === "dynamic") return null;
  const qs = a.questions || [];
  if (a.deliverCount > 0 && a.deliverCount < qs.length) return null;
  return qs.reduce((sum, qi) => sum + questionMarks(qi), 0);
}

/** A coding problem as the paper shows it — never its solution or test data. */
function codingLine(c) {
  const p = c.question && typeof c.question === "object" ? c.question : null;
  if (!p) return { title: "Coding problem", meta: "Deleted from the coding bank", missing: true };
  const tests = (p.testCases || []).filter((t) => String(t?.expectedOutput ?? "").trim() !== "");
  const samples = tests.filter((t) => !t.isHidden).length;
  const hidden = tests.length - samples;
  const testText = [samples ? `${samples} sample` : "", hidden ? `${hidden} hidden` : ""].filter(Boolean).join(" + ");
  return {
    title: p.title || "Coding problem",
    meta: [
      DIFFICULTY_LABELS[String(p.difficulty || "").toLowerCase()],
      (p.categories || []).map((l) => l?.name).filter(Boolean).join(", "),
      testText ? `${testText} test${tests.length === 1 ? "" : "s"}` : "No tests",
    ].filter(Boolean).join(" · "),
    missing: false,
  };
}

/** A card on this page: a titled panel with an optional line under the title. */
function DetailCard({ icon: Icon, title, description, children, contentClassName = "" }) {
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          {Icon && <Icon size={16} aria-hidden="true" />}
          {title}
        </AppCardTitle>
        {description && <AppCardDescription>{description}</AppCardDescription>}
      </AppCardHeader>
      <AppCardContent className={contentClassName}>{children}</AppCardContent>
    </AppCard>
  );
}

function DifficultyText({ level }) {
  if (!level) return <span className="tw-text-content-subtle">—</span>;
  return <span className="tw-text-[0.8125rem] tw-text-content-muted">{DIFFICULTY_LABELS[level] || level}</span>;
}

/** The hand-picked questions of a paper or a section. */
function QuestionsTable({ items }) {
  const rows = items.map((qi, i) => ({ ...qi, key: idOf(qi.question) || String(i), index: i }));
  return (
    <AppDataTable
      fill={false}
      rows={rows}
      rowKey={(r) => r.key}
      emptyMessage="No questions"
      columns={[
        { key: "index", label: "#", align: "right", render: (r) => <span className="tw-tabular-nums tw-text-content-subtle">{r.index + 1}</span> },
        {
          key: "text",
          label: "Question",
          render: (r) => <span className="tw-line-clamp-2 tw-text-content">{r.question?.questionText || "Deleted from the question bank"}</span>,
        },
        {
          key: "type",
          label: "Type",
          render: (r) => (r.question?.questionType
            ? <AppBadge variant="outline" size="sm">{QUESTION_TYPE_LABELS[r.question.questionType] || r.question.questionType}</AppBadge>
            : null),
        },
        { key: "difficulty", label: "Difficulty", render: (r) => <DifficultyText level={r.question?.difficulty} /> },
        { key: "marks", label: "Marks", align: "right", render: (r) => <strong className="tw-tabular-nums">{questionMarks(r)}</strong> },
      ]}
    />
  );
}

/** A random draw's rules. Categories are named from the quiz bank's list. */
function RulesTable({ rules, categoryName }) {
  const any = <span className="tw-italic tw-text-content-subtle">Any</span>;
  const rows = rules.map((r, i) => ({ ...r, key: r._id || String(i) }));
  return (
    <AppDataTable
      fill={false}
      rows={rows}
      rowKey={(r) => r.key}
      columns={[
        { key: "category", label: "Category", render: (r) => (r.category ? categoryName(r.category) : any) },
        { key: "subcategory", label: "Subtopic", render: (r) => (r.subcategory ? categoryName(r.subcategory) : any) },
        { key: "difficulty", label: "Difficulty", render: (r) => (r.difficulty ? <DifficultyText level={r.difficulty} /> : any) },
        {
          key: "tags",
          label: "Tags",
          render: (r) => (r.tags?.length ? (
            <span className="tw-flex tw-flex-wrap tw-gap-1">
              {r.tags.map((t) => <AppBadge key={t} variant="secondary" size="sm">{t}</AppBadge>)}
            </span>
          ) : <span className="tw-text-content-subtle">—</span>),
        },
        { key: "count", label: "Count", align: "right", render: (r) => <strong className="tw-tabular-nums">{r.count}</strong> },
      ]}
    />
  );
}

function CodingList({ items }) {
  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-0">
      {items.map((c, i) => {
        const line = codingLine(c);
        return (
          <li key={idOf(c.question) || i} className="tw-flex tw-items-center tw-gap-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
            <Code2 size={16} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
              <span className="tw-text-[0.8125rem] tw-font-medium tw-text-content">{line.title}</span>
              <span className={line.missing ? "tw-text-xs tw-text-danger" : "tw-text-xs tw-text-content-subtle"}>{line.meta}</span>
            </span>
            <span className="tw-shrink-0 tw-text-[0.8125rem] tw-tabular-nums tw-text-content">{plural(Number(c.marks ?? 10), "mark")}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * AssessmentDetailsPage — one assessment as staff see it: its paper (flat, a
 * random draw, or sections with their questions and coding problems), its rules,
 * who it is for, and the actions on it. `/assessments/:id`.
 *
 * The builder lands here after a save with `location.state.notice`, shown once.
 */
const AssessmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasPlanFeature = useHasPlanFeature();

  const [assessment, setAssessment] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [categories, setCategories] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [notice, setNotice] = useState(() => location.state?.notice || "");

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  // Shown once: a reload of this page must not announce the save again.
  useEffect(() => {
    if (location.state?.notice) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssessment = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoadState("loading");
    try {
      const { data } = await apiClient.get(`/assessments/${id}`);
      setAssessment(data.assessment);
      setLoadState("ready");
    } catch (err) {
      if (quiet) {
        showToast(err.response?.data?.message || "Failed to refresh", "error");
        return;
      }
      setLoadState(err?.response?.status === 404 ? "missing" : "error");
    }
  }, [id]);

  useEffect(() => { fetchAssessment(); }, [fetchAssessment]);

  // Random-draw rules store category ids; the quiz bank's list names them.
  useEffect(() => {
    let alive = true;
    apiClient.get("/quiz-categories", { params: { flat: true } })
      .then(({ data }) => { if (alive) setCategories(data.categories || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const categoryName = useMemo(() => {
    const names = new Map(categories.map((c) => [String(c._id), c.name]));
    return (ref) => ref?.name || names.get(idOf(ref)) || "Category";
  }, [categories]);

  const handlePublish = async () => {
    setActionBusy(true);
    try {
      await apiClient.put(`/assessments/${id}/publish`);
      showToast("Assessment published");
      await fetchAssessment({ quiet: true });
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to publish", "error");
    } finally {
      setActionBusy(false);
      setPublishConfirm(false);
    }
  };

  const handleArchive = async () => {
    setActionBusy(true);
    try {
      await apiClient.put(`/assessments/${id}/archive`);
      showToast("Assessment archived");
      await fetchAssessment({ quiet: true });
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to archive", "error");
    } finally {
      setActionBusy(false);
      setArchiveConfirm(false);
    }
  };

  const handleDuplicate = async () => {
    setActionBusy(true);
    try {
      const { data } = await apiClient.post(`/assessments/${id}/duplicate`);
      showToast("Duplicated");
      setTimeout(() => navigate(`/assessments/${data.assessment._id}`), 500);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to duplicate", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    setActionBusy(true);
    try {
      await apiClient.delete(`/assessments/${id}`);
      showToast("Deleted");
      setTimeout(() => navigate("/assessments"), 500);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete", "error");
    } finally {
      setActionBusy(false);
      setDeleteConfirm(false);
    }
  };

  const totalMarks = useMemo(() => (assessment ? paperMarks(assessment) : null), [assessment]);

  // ── Loading and failure ─────────────────────────────────────────────
  if (loadState === "loading") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assessment">
        <PageHeader breadcrumbs={assessmentTrail({ id })} title="Assessment" />
        <Shimmer height={36} />
        <div className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2 min-[1200px]:tw-grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Shimmer key={i} height={88} />)}
        </div>
        <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_21.25rem]">
          <Shimmer height={360} />
          <Shimmer height={360} />
        </div>
      </div>
    );
  }

  if (loadState === "missing" || loadState === "error" || !assessment) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <PageHeader breadcrumbs={assessmentTrail({ id })} title="Assessment" />
        {loadState === "error" ? (
          <Callout
            tone="danger"
            role="alert"
            action={<AppButton size="xs" variant="outline" onClick={() => fetchAssessment()}>Try again</AppButton>}
          >
            The assessment could not be loaded.
          </Callout>
        ) : (
          <EmptyState
            icon={HelpCircle}
            title="Assessment not found"
            description="It may have been deleted or you don't have access."
            action={<AppButton variant="outline" onClick={() => navigate("/assessments")}>Back to list</AppButton>}
          />
        )}
      </div>
    );
  }

  const a = assessment;
  const status = STATUS[a.status] || STATUS.draft;
  const canDelete = (a.totalAttempts || 0) === 0;
  const sectioned = !!a.isSectioned && (a.sections || []).length > 0;
  const sequential = sectioned && a.sectionNavigation === "sequential";
  const sections = sectioned ? [...a.sections].sort((x, y) => (x.order || 0) - (y.order || 0)) : [];
  const codingCount = sections.reduce((sum, s) => sum + (s.codingQuestions || []).length, 0);
  const ac = a.antiCheat || {};
  const anyGuard = ac.tabSwitchDetection || ac.fullscreenRequired || ac.disableRightClick || ac.disableCopyPaste;
  // Run only through public exam links, whose own settings guard the exam.
  const publicOnly = a.assignmentType === "public";

  const headerActions = (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
      <AppButton variant="outline" size="sm" onClick={() => navigate(`/assessments/${id}/preview`)}>
        <Eye size={16} aria-hidden="true" /> Preview as a student
      </AppButton>
      <AppButton variant="outline" size="sm" onClick={() => navigate(`/assessments/edit/${id}`)}>
        <Pencil size={16} aria-hidden="true" /> Edit
      </AppButton>
      <AppButton variant="outline" size="sm" onClick={handleDuplicate} disabled={actionBusy}>
        <Copy size={16} aria-hidden="true" /> Duplicate
      </AppButton>
      {a.status === "draft" && (
        <AppButton size="sm" onClick={() => setPublishConfirm(true)} disabled={actionBusy}>
          <Send size={16} aria-hidden="true" /> Publish
        </AppButton>
      )}
      {a.status === "published" && hasPlanFeature("publicExams") && (
        <AppButton
          variant={publicOnly ? "default" : "outline"}
          size="sm"
          onClick={() => navigate(`/public-exams/new?assessment=${id}`)}
        >
          <Globe size={16} aria-hidden="true" /> Use in a public exam
        </AppButton>
      )}
      {a.status === "published" && (
        <AppButton variant="secondary" size="sm" onClick={() => setArchiveConfirm(true)} disabled={actionBusy}>
          <Archive size={16} aria-hidden="true" /> Archive
        </AppButton>
      )}
      {canDelete && (
        <IconButton
          icon={Trash2}
          label="Delete assessment"
          variant="ghost-destructive"
          size="sm"
          onClick={() => setDeleteConfirm(true)}
          disabled={actionBusy}
        />
      )}
      <IconButton
        icon={RefreshCcw}
        label="Refresh"
        size="sm"
        onClick={() => fetchAssessment({ quiet: true })}
        disabled={actionBusy}
      />
    </div>
  );

  const structureBadges = sectioned
    ? [plural(sections.length, "section"), sequential ? "Timed sections in order" : null].filter(Boolean)
    : [a.selectionMode === "dynamic" ? "Random draw" : "Hand-picked questions",
      a.deliverCount > 0 ? `Random ${a.deliverCount} per student` : null].filter(Boolean);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        breadcrumbs={assessmentTrail({ id, title: a.title })}
        title={a.title}
        description={a.description}
        actions={headerActions}
      />

      {notice && (
        <Callout
          tone="success"
          role="status"
          action={<IconButton icon={X} label="Dismiss" size="xs" onClick={() => setNotice("")} />}
        >
          {notice}
        </Callout>
      )}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <StatusBadge status={status.tone}>{status.label}</StatusBadge>
        <AppBadge variant="outline" size="sm">{MODE_LABELS[a.mode] || a.mode}</AppBadge>
        {structureBadges.map((b) => <AppBadge key={b} variant="secondary" size="sm">{b}</AppBadge>)}
        {a.publishedAt && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
            <Calendar size={12} aria-hidden="true" /> Published {day(a.publishedAt)}
          </span>
        )}
        {a.updatedAt && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
            <RefreshCcw size={12} aria-hidden="true" /> Updated {day(a.updatedAt)}
          </span>
        )}
      </div>

      <StatsGrid cols={4}>
        <StatsCard
          title="Questions"
          value={a.questionCount || 0}
          subtitle={codingCount ? `incl. ${plural(codingCount, "coding problem")}` : undefined}
          icon={HelpCircle}
          color="blue"
        />
        <StatsCard
          title="Total marks"
          value={totalMarks == null ? "Varies" : totalMarks}
          subtitle={totalMarks == null ? "Depends on the questions drawn" : undefined}
          icon={BarChart3}
          color="emerald"
        />
        <StatsCard
          title="Time limit"
          value={a.timeLimit > 0 ? `${a.timeLimit}m` : "No limit"}
          subtitle={sequential ? "Timed per section" : undefined}
          icon={Clock}
          color="amber"
        />
        <StatsCard title="Attempts" value={a.totalAttempts || 0} icon={Users} color="rose" />
      </StatsGrid>

      <div className="tw-grid tw-items-start tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_21.25rem]">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4">
          {a.instructions && (
            <DetailCard icon={Eye} title="Instructions">
              <LinkedText text={a.instructions} className="tw-text-sm tw-leading-relaxed tw-text-content" />
            </DetailCard>
          )}

          {!sectioned && a.selectionMode !== "dynamic" && (
            <DetailCard
              icon={ListChecks}
              title="Questions"
              description={a.questions?.length ? `${a.questions.length} total` : undefined}
            >
              {a.questions?.length ? (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {a.deliverCount > 0 && a.deliverCount < a.questions.length && (
                    <Callout tone="info">
                      Each student gets {a.deliverCount} of these {a.questions.length} questions, at random.
                    </Callout>
                  )}
                  <QuestionsTable items={a.questions} />
                </div>
              ) : (
                <EmptyState
                  icon={ListChecks}
                  title="No questions added yet"
                  description="Edit this assessment to add questions."
                  action={(
                    <AppButton variant="outline" onClick={() => navigate(`/assessments/edit/${id}`)}>
                      <Pencil size={16} aria-hidden="true" /> Edit to add questions
                    </AppButton>
                  )}
                />
              )}
            </DetailCard>
          )}

          {!sectioned && a.selectionMode === "dynamic" && (
            <DetailCard
              icon={Shuffle}
              title="Random draw"
              description={`${a.dynamicConfig?.totalQuestions || ruleTotal(a.dynamicConfig?.rules)} questions per attempt`}
            >
              {a.dynamicConfig?.rules?.length ? (
                <RulesTable rules={a.dynamicConfig.rules} categoryName={categoryName} />
              ) : (
                <EmptyState
                  icon={Settings}
                  title="No rules configured"
                  description="Edit this assessment to add the rules for the draw."
                />
              )}
            </DetailCard>
          )}

          {sectioned && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <Layers size={16} aria-hidden="true" /> Sections
                </AppCardTitle>
                <AppCardDescription>
                  {plural(sections.length, "section")} ·{" "}
                  {sequential ? "Sections run in order, each on its own timer" : "Students move between sections freely"}
                </AppCardDescription>
              </AppCardHeader>
              <AppCardContent>
                <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-6 tw-p-0">
                  {sections.map((s, i) => {
                    const rules = s.dynamicConfig?.rules || [];
                    const random = rules.length > 0;
                    const questions = s.questions || [];
                    const coding = s.codingQuestions || [];
                    const meta = [
                      random
                        ? `Random draw: ${plural(s.dynamicConfig?.totalQuestions || ruleTotal(rules), "question")}`
                        : questions.length ? plural(questions.length, "question") : "",
                      coding.length ? plural(coding.length, "coding problem") : "",
                      sequential && s.timeLimit > 0 ? `${s.timeLimit} min` : "",
                    ].filter(Boolean).join(" · ") || "Empty";
                    return (
                      <li key={s._id || i} className="tw-flex tw-flex-col tw-gap-3">
                        <div className="tw-flex tw-items-start tw-gap-3">
                          <SectionMark letter={letterOf(i)} />
                          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                            <h4 className="tw-m-0 tw-text-[0.9375rem] tw-font-semibold tw-text-content">{s.title}</h4>
                            <span className="tw-text-xs tw-text-content-muted">{meta}</span>
                            {s.description && (
                              <LinkedText text={s.description} as="p" className="tw-m-0 tw-mt-1 tw-text-[0.8125rem] tw-text-content-muted" />
                            )}
                          </div>
                        </div>
                        {random && <RulesTable rules={rules} categoryName={categoryName} />}
                        {!random && questions.length > 0 && <QuestionsTable items={questions} />}
                        {coding.length > 0 && <CodingList items={coding} />}
                      </li>
                    );
                  })}
                </ol>
              </AppCardContent>
            </AppCard>
          )}
        </div>

        <aside className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4" aria-label="Assessment settings">
          <DetailCard icon={Settings} title="Settings">
            <DetailSection>
              <DetailRow label="Passing %" value={a.passingPercentage > 0 ? `${a.passingPercentage}%` : "—"} />
              <DetailRow label="Max attempts" value={a.maxAttempts > 0 ? a.maxAttempts : "Unlimited"} />
              <DetailRow
                label="Negative marking"
                value={a.negativeMarking
                  ? (a.negativeMarkValue > 0 ? `Yes, −${a.negativeMarkValue} per wrong answer` : "Yes")
                  : "No"}
              />
              {a.partialMarking && <DetailRow label="Partial credit" value="Yes" />}
              <DetailRow label="Shuffle questions" value={yesNo(a.shuffleQuestions)} />
              <DetailRow label="Shuffle options" value={yesNo(a.shuffleOptions)} />
              <DetailRow label="Allow review" value={yesNo(a.allowReview)} />
              <DetailRow label="Show correct answers" value={yesNo(a.showCorrectAnswers)} />
              <DetailRow label="Explanations" value={yesNo(a.showExplanation)} />
            </DetailSection>
          </DetailCard>

          {!publicOnly && (a.mode === "exam" || a.mode === "sectioned_exam" || anyGuard) && a.antiCheat && (
            <DetailCard icon={Shield} title="Exam security">
              <DetailSection>
                <DetailRow label="Tab switching" value={ac.tabSwitchDetection ? `Watched (max ${ac.maxTabSwitches})` : "No"} />
                <DetailRow label="Full screen" value={ac.fullscreenRequired ? "Required" : "No"} />
                <DetailRow label="Right click" value={ac.disableRightClick ? "Blocked" : "Allowed"} />
                <DetailRow label="Copy / paste" value={ac.disableCopyPaste ? "Blocked" : "Allowed"} />
                <DetailRow label="Auto-submit" value={yesNo(ac.autoSubmitOnViolation)} />
              </DetailSection>
            </DetailCard>
          )}

          <DetailCard icon={Users} title="Assignment">
            <div className="tw-flex tw-flex-col tw-gap-3">
              <DetailSection>
                <DetailRow label="Given to" value={AUDIENCE_LABELS[a.assignmentType] || a.assignmentType || "—"} />
                {a.assignmentType === "direct" && (
                  <DetailRow label="Students" value={plural((a.assignedStudents || []).length, "student")} />
                )}
                {a.assignmentType === "rules" && (
                  <DetailRow label="Groups" value={plural((a.assignmentRules || []).length, "group")} />
                )}
                {a.startDate && <DetailRow label="Start" value={new Date(a.startDate).toLocaleString()} />}
                {a.endDate && <DetailRow label="End" value={new Date(a.endDate).toLocaleString()} />}
              </DetailSection>
              {a.assignedBatches?.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-text-xs tw-font-medium tw-text-content-muted">Batches</span>
                  <span className="tw-flex tw-flex-wrap tw-gap-1">
                    {a.assignedBatches.map((b, i) => <AppBadge key={idOf(b) || i} variant="secondary" size="sm">{b?.name || b}</AppBadge>)}
                  </span>
                </div>
              )}
              {a.assignedCourses?.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-text-xs tw-font-medium tw-text-content-muted">Courses</span>
                  <span className="tw-flex tw-flex-wrap tw-gap-1">
                    {a.assignedCourses.map((c, i) => (
                      <AppBadge key={idOf(c) || i} variant="secondary" size="sm">{c?.title || c?.name || c}</AppBadge>
                    ))}
                  </span>
                </div>
              )}
            </div>
          </DetailCard>

          <DetailCard icon={Tag} title="Metadata">
            <DetailSection>
              <DetailRow label="Created" value={day(a.createdAt)} />
              <DetailRow label="Last updated" value={day(a.updatedAt)} />
              <DetailRow label="Average score" value={a.averageScore > 0 ? `${a.averageScore.toFixed(1)}%` : "—"} />
              <DetailRow label="ID" value={<span className="tw-break-all tw-font-mono tw-text-xs">{a._id}</span>} />
            </DetailSection>
          </DetailCard>
        </aside>
      </div>

      <ConfirmModal
        show={publishConfirm}
        onHide={() => setPublishConfirm(false)}
        onConfirm={handlePublish}
        title="Publish assessment?"
        message={publicOnly
          ? "It becomes ready to use in a public exam. Your students won't see it, and nobody is notified."
          : "Once published, students will be able to see and attempt this assessment."}
        confirmText="Publish"
        variant="info"
        loading={actionBusy}
      />
      <ConfirmModal
        show={archiveConfirm}
        onHide={() => setArchiveConfirm(false)}
        onConfirm={handleArchive}
        title="Archive assessment?"
        message="Students will no longer see this assessment. You can restore it later."
        confirmText="Archive"
        variant="warning"
        loading={actionBusy}
      />
      <ConfirmModal
        show={deleteConfirm}
        onHide={() => setDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete assessment?"
        message="This will permanently delete the assessment. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        loading={actionBusy}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default AssessmentDetailsPage;
